import * as React from 'react';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { refreshDataSources } from 'src/actions/data-sources/action-creators/refreshDataSources';
import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type UseSampleMusicDataSourceResult = {
  /**
   * Creates the sample music data source if it doesn't exist,
   * or refreshes its tables if it already exists.
   * Returns the data source on success, or null on failure.
   */
  createOrRefresh: () => Promise<DataSource | null>;
  /** Whether a create/refresh operation is in progress */
  isLoading: boolean;
  /** The existing sample music data source, if it exists */
  existingDataSource: DataSource | undefined;
};

/**
 * Hook to manage the sample music data source lifecycle.
 * Provides utilities to create or refresh the sample data source,
 * and tracks loading state.
 */
export const useSampleMusicDataSource = (): UseSampleMusicDataSourceResult => {
  const dispatch = useReduxDispatch();
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const [isLoading, setIsLoading] = React.useState(false);

  const existingDataSource = React.useMemo(
    () =>
      dataSourcesByType[SAMPLE_MUSIC_DATA_SOURCE.type]?.find(
        ({ id }) => id === SAMPLE_MUSIC_DATA_SOURCE.id
      ),
    [dataSourcesByType]
  );

  const createOrRefresh =
    React.useCallback(async (): Promise<DataSource | null> => {
      setIsLoading(true);
      try {
        if (existingDataSource) {
          // Data source exists - just refresh its tables
          await dispatch(refreshDataSourceTables(SAMPLE_MUSIC_DATA_SOURCE.id));
          return existingDataSource;
        }

        // Create the sample data source
        const didSucceed = await dispatch(
          addDataSource(SAMPLE_MUSIC_DATA_SOURCE)
        );
        if (didSucceed) {
          // Refresh data sources to get the newly created one
          await dispatch(refreshDataSources());
          // Refresh tables
          await dispatch(refreshDataSourceTables(SAMPLE_MUSIC_DATA_SOURCE.id));
          // Return the sample data source definition
          // The caller should re-fetch from Redux to get the updated data source with tables
          return SAMPLE_MUSIC_DATA_SOURCE;
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [dispatch, existingDataSource]);

  return { createOrRefresh, isLoading, existingDataSource };
};
